import * as React from 'react';
import { FC } from 'react';
import {
  Badge,
  Black,
  Blue100,
  Button,
  FileUpload,
  FileUploadItem,
  Gold0,
  Gold20,
  Gold90,
  Red90,
  TextInput,
  message,
  typeCompact12RegularProperties,
  typeCompact14MediumProperties,
} from '@traceair/tds';
import { classes, stylesheet } from 'typestyle';
import { PlusIcon } from '@traceair/webapp-icons';
import { InvoiceSubmission, InvoicingFixValues, GcpMaterialEntry } from './types';
import { SectionHeader } from './components/SectionHeader';
import { InvoicePageHeader } from './components/InvoicePageHeader';
import {
  TimeMaterialsDateHeader,
  TimeMaterialsCategoryHeader,
} from './components/time_materials/TimeMaterialsDateGroup';
import { OtherItemsList } from './components/OtherItemsList';
import { ItemRow } from './components/ItemRow';
import { GcpMaterialRow } from './components/GcpMaterialRow';
import { ExtraCostMenuButton } from './components/ExtraCostMenuButton';
import { PaymentSection } from './components/PaymentSection';
import getPilotApiService from '../../../api_services/PilotApiService';
import type {
  AssociatedMission,
  AssociatedMissionPayload,
  TimeMaterialsEntryPayload,
} from '../../../api_services/types';
import Logger from '../../../libs/logger/Logger';
import {
  numericOnly,
  buildFixDateGroups,
  buildSections,
  groupBySiteSummary,
  getStatusBadgeProps,
  buildCustomRows,
  buildConsultantGcpRateInfo,
  getGcpItemAmount,
  isGcpRateFullySet,
  buildFlightsSubtitle,
  buildGcpSubtitle,
  buildTimeMaterialsAmountsByDate,
  isFixedFeeInvoice,
  toGcpCostBasis,
  toGcpExtraCosts,
  collectMissionRefIds,
  buildTimeMaterialsDayEntries,
  hasDerivedGcpMaterialsCosts,
  sumGcpExtraCosts,
} from './InvoicingUtils';
import { useGcpItemPrices } from './hooks/useGcpItemPrices';
import { useGcpMaterialEntries } from './hooks/useGcpMaterialEntries';
import { TimeMaterialsDatePrices } from './hooks/useTimeMaterialsDateGroups';
import { sharedInvoicingCss } from './styles/shared';
import { trackAction } from '../../../libs/analytics/ActionTracking';

type Props = {
  invoice: InvoiceSubmission;
  onBack: () => void;
};

export const InvoiceDetailEditableView: FC<Props> = ({ invoice, onBack }) => {
  const raw = invoice.rawInvoice;

  const [isSubmittingFix, setIsSubmittingFix] = React.useState(false);
  const [fixSubmitAttempted, setFixSubmitAttempted] = React.useState(false);
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});

  const [fixPdfUploading, setFixPdfUploading] = React.useState(false);
  const [fixPdfLink, setFixPdfLink] = React.useState<string | undefined>(raw?.invoiceDoc?.url);
  const [fixPdfName, setFixPdfName] = React.useState<string | undefined>(raw?.invoiceDoc?.filename);
  const [fixPdfSize, setFixPdfSize] = React.useState<number | undefined>(raw?.invoiceDoc?.size);
  const [fixPdfType, setFixPdfType] = React.useState<string | undefined>(raw?.invoiceDoc?.type);
  // Stable S3 key of the stored document (invoiceDoc.path). While set, resubmit sends it as
  // `invoicePath` — "keep the stored document" — so the service doesn't have to rely on
  // recognizing the echoed display url. Cleared once the pilot replaces or removes the file:
  // from that point the payload describes a new document (or none), not the stored one.
  const [fixPdfPath, setFixPdfPath] = React.useState<string | undefined>(raw?.invoiceDoc?.path);
  const fixPdfLinkRef = React.useRef<string | undefined>(raw?.invoiceDoc?.url);
  fixPdfLinkRef.current = fixPdfLink;
  // Upload generation counter + mount flag, mirroring useInvoiceForm's handlePdfChange. The remove
  // button stays live while an upload is in flight, so without these an unawaited upload resolving
  // after a remove (or after leaving the page) would reattach the file, pass the fixPdfLinkRef
  // submit gate, and persist a document the user had already taken off the invoice.
  const fixPdfRequestRef = React.useRef(0);
  const isMount = React.useRef(true);

  React.useEffect(
    () => () => {
      isMount.current = false;
    },
    [],
  );

  async function handleFixPdfChange(file: File) {
    trackAction('Upload invoice PDF');
    const requestId = ++fixPdfRequestRef.current;
    const isCurrent = () => isMount.current && fixPdfRequestRef.current === requestId;

    setFixPdfName(file.name);
    setFixPdfSize(file.size);
    setFixPdfType(file.type);
    setFixPdfLink(undefined);
    setFixPdfPath(undefined);
    setFixPdfUploading(true);
    try {
      const url = await getPilotApiService().uploadInvoice(file);
      if (isCurrent()) setFixPdfLink(url);
    } catch (err) {
      Logger.error(err as Error | string);
      if (isCurrent()) {
        setFixPdfName(undefined);
        setFixPdfSize(undefined);
        setFixPdfType(undefined);
      }
    }
    if (isCurrent()) setFixPdfUploading(false);
  }

  function handleFixPdfDelete() {
    trackAction('Delete invoice PDF');
    // Bumping the counter disowns any upload still in flight, so it can't reattach what's removed.
    fixPdfRequestRef.current++;
    setFixPdfName(undefined);
    setFixPdfSize(undefined);
    setFixPdfType(undefined);
    setFixPdfLink(undefined);
    setFixPdfPath(undefined);
    setFixPdfUploading(false);
  }

  const [fixValues, setFixValues] = React.useState<InvoicingFixValues>(() => {
    const st = raw?.submittedTotals;
    return {
      scanCosts: st?.scanCosts != null ? String(st.scanCosts) : '',
      panoCosts: st?.panoCosts != null ? String(st.panoCosts) : '',
      lidarCosts: st?.lidarCosts != null ? String(st.lidarCosts) : '',
      gcpCosts: st?.gcpServicesCosts != null ? String(st.gcpServicesCosts) : '',
      gcpMaterialsCosts: st?.gcpMaterialsCosts != null ? String(st.gcpMaterialsCosts) : '',
      equipmentLeasingExpenses: st?.equipmentLeasingExpenses != null ? String(st.equipmentLeasingExpenses) : '',
      paymentLink: raw?.paymentLink ?? '',
      bankDetails: raw?.bankDetails ?? '',
      invoiceLink: raw?.invoiceDoc?.url ?? '',
    };
  });

  const [paymentMethod, setPaymentMethod] = React.useState<'link' | 'bank'>(() => (raw?.bankDetails ? 'bank' : 'link'));

  const [fixOtherItems, setFixOtherItems] = React.useState<Array<{ id: string; label: string; price: string }>>(() => {
    if (raw?.otherCharges?.length) {
      return raw.otherCharges.map((item, idx) => ({ id: String(idx), label: item.label, price: String(item.price) }));
    }
    return [{ id: '1', label: '', price: '' }];
  });

  const addFixOtherItem = () => setFixOtherItems((prev) => [...prev, { id: String(Date.now()), label: '', price: '' }]);
  const removeFixOtherItem = (id: string) => setFixOtherItems((prev) => prev.filter((i) => i.id !== id));
  const updateFixOtherLabel = (id: string, value: string) =>
    setFixOtherItems((prev) => prev.map((i) => (i.id === id ? { ...i, label: value } : i)));
  const updateFixOtherPrice = (id: string, value: string) =>
    setFixOtherItems((prev) => prev.map((i) => (i.id === id ? { ...i, price: numericOnly(value) } : i)));

  const [fixDatePrices, setFixDatePrices] = React.useState<Record<string, TimeMaterialsDatePrices>>(() => {
    if (!raw) return {};
    // Prefill each date's "Scans, panoramas price" and "Mileage" from `timeMaterialsEntries`.
    // Without this, resubmit would send these as 0 and silently zero out the per-day T&M costs.
    const { flightsCostsByDate, mileageByDate } = buildTimeMaterialsAmountsByDate(raw.timeMaterialsEntries);
    const result: Record<string, TimeMaterialsDatePrices> = {};
    for (const m of raw.associatedMissions) {
      if (!result[m.flightDate]) {
        result[m.flightDate] = {
          flightsCosts: flightsCostsByDate[m.flightDate] ?? '',
          mileage: mileageByDate[m.flightDate] ?? '',
        };
      }
    }
    return result;
  });

  // Each GCP service item supports one "GCP Materials" and one "Mileage" extra cost entry, keyed
  // by the item's gcpServiceId (or its synthesized id as a fallback) — pre-populated from any
  // extra costs already saved on the invoice so office staff can see/edit what a pilot submitted.
  // Grouped by the owning mission's gcpServiceId: the entries pilot-api builds carry no id of
  // their own, and for a gcp_services mission that field is derived from its airtableRecordId on
  // every response, so it's present regardless of when the invoice was submitted.
  const {
    entries: gcpMaterialEntries,
    addGcpMaterial,
    removeGcpMaterial,
    updateGcpMaterialAmount,
    hasEntries: hasGcpMaterialEntries,
    total: gcpMaterialEntriesTotal,
  } = useGcpMaterialEntries(() => {
    if (!raw) return {};
    const result: Record<string, GcpMaterialEntry[]> = {};
    for (const m of raw.associatedMissions) {
      const savedExtraCosts = m.extraCosts ?? [];

      if (!savedExtraCosts.length) continue;
      // pilot-api derives gcpServiceId from airtableRecordId for every gcp_services mission, so a
      // mission with saved extra costs should always have one — but that invariant lives in another
      // service and can't be enforced here. Without a key these rows have nothing to group under:
      // they'd be invisible in the form and absent from the resubmit payload, quietly dropping
      // billed amounts. Report it instead of swallowing it.
      if (!m.gcpServiceId) {
        Logger.error('Invoice GCP mission has extraCosts but no gcpServiceId — saved materials/mileage dropped', {
          invoiceId: raw.id,
          airtableRecordId: m.airtableRecordId,
          category: m.category,
        });
        continue;
      }
      for (const [idx, e] of savedExtraCosts.entries()) {
        (result[m.gcpServiceId] ??= []).push({
          id: `${m.gcpServiceId}-${idx}`,
          type: e.description === 'Mileage' ? 'mileage' : 'materials',
          amount: String(e.amount),
        });
      }
    }
    return result;
  });

  const isFixedFee = isFixedFeeInvoice(raw);
  const customRows = React.useMemo(() => (raw ? buildCustomRows(raw) : []), [raw]);
  const fixDateGroups = React.useMemo(() => (!isFixedFee && raw ? buildFixDateGroups(raw) : []), [raw, isFixedFee]);
  const sections = React.useMemo(() => (isFixedFee && raw ? buildSections(raw) : []), [raw, isFixedFee]);

  // Per-item GCP price overrides — each item defaults to its backend-calculated amount
  // (hourly rate × confirmed hours, or raw cost as a fallback) but can be edited individually,
  // for both fixed-fee and time & materials invoices.
  // A rate × hours item is `locked`: this view renders it read-only (see the amountInput guards
  // below), so it resubmits its calculated amount and never a price-map override.
  const gcpPriceableItems = React.useMemo((): Array<{
    id: string;
    defaultAmount: number;
    locked: boolean;
    airtableRecordId?: string;
  }> => {
    if (isFixedFee) {
      return (sections.find((s) => s.id === 'gcp_services')?.items ?? [])
        .filter((i) => i.status !== 'failed')
        .map((i) => ({
          id: i.id,
          defaultAmount: i.amount ?? 0,
          locked: isGcpRateFullySet(i.consultantGcpServiceHourlyRate, i.consultantGcpServiceTimeInHours),
          airtableRecordId: i.airtableRecordId,
        }));
    }
    return fixDateGroups
      .flatMap((g) => g.gcpItems)
      .filter((i) => !i.isFailed)
      .map((i) => ({
        id: i.id,
        defaultAmount: getGcpItemAmount(i.cost, i.consultantGcpServiceHourlyRate, i.consultantGcpServiceTimeInHours),
        locked: isGcpRateFullySet(i.consultantGcpServiceHourlyRate, i.consultantGcpServiceTimeInHours),
        airtableRecordId: i.airtableRecordId,
      }));
  }, [isFixedFee, sections, fixDateGroups]);

  const gcpDefaultAmountById = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const item of gcpPriceableItems) map.set(item.id, item.defaultAmount);
    return map;
  }, [gcpPriceableItems]);

  const {
    prices: gcpItemPrices,
    updatePrice: updateGcpItemPrice,
    getPrice: getGcpItemPrice,
    total: gcpServicesTotal,
  } = useGcpItemPrices(gcpPriceableItems);

  // Keyed by dateKey so each T&M date group's live (override-aware) GCP total is computed
  // once per render and reused across the date total, category header, and submit payload.
  const gcpGroupTotals = React.useMemo(() => {
    const totals = new Map<string, number>();
    for (const group of fixDateGroups) {
      const total = group.gcpItems
        .filter((i) => !i.isFailed)
        .reduce((s, i) => s + getGcpItemPrice(i.id, gcpDefaultAmountById.get(i.id) ?? 0), 0);
      totals.set(group.dateKey, total);
    }
    return totals;
  }, [fixDateGroups, getGcpItemPrice, gcpDefaultAmountById]);

  // `gcpMaterialEntries` is keyed by whatever the render sites below pass to addGcpMaterial —
  // `item.gcpServiceId ?? item.id`. The submit payload iterates missions instead, so it needs the
  // reverse lookup: mission's airtableRecordId → that item's entry key. Built from the same
  // sections/date groups the rows render from, and deliberately unfiltered by failed status —
  // already-saved entries stay visible (and editable) on a failed item even though no new ones
  // can be added to it, so they must still round-trip.
  const gcpEntryKeyByRecordId = React.useMemo(() => {
    const map = new Map<string, string>();
    const items: Array<{ id: string; gcpServiceId?: string; airtableRecordId?: string }> = isFixedFee
      ? sections.find((s) => s.id === 'gcp_services')?.items ?? []
      : fixDateGroups.flatMap((g) => g.gcpItems);
    for (const item of items) {
      if (item.airtableRecordId) map.set(item.airtableRecordId, item.gcpServiceId ?? item.id);
    }
    return map;
  }, [isFixedFee, sections, fixDateGroups]);

  // Keyed by dateKey so the sum of a date's GCP Materials/Mileage entries is computed once per
  // render instead of being re-derived on every access (date total, category header amount).
  const gcpMaterialsTotalsByDate = React.useMemo(() => {
    const totals = new Map<string, number>();
    for (const group of fixDateGroups) {
      const total = group.gcpItems
        .map((item) => item.gcpServiceId ?? item.id)
        .flatMap((key) => gcpMaterialEntries[key] ?? [])
        .reduce((s, e) => s + parseFloat(e.amount || '0'), 0);
      totals.set(group.dateKey, total);
    }
    return totals;
  }, [fixDateGroups, gcpMaterialEntries]);

  const fixOtherTotal = fixOtherItems.reduce((s, i) => s + parseFloat(i.price || '0'), 0);
  const isSingleBlankFixItem = fixOtherItems.length === 1 && !fixOtherItems[0].label.trim() && !fixOtherItems[0].price;

  // Newer invoices attach materials/mileage extra costs to individual GCP service items instead of
  // one lump sum, and those rows replace the legacy manual field. Anchored on the invoice as loaded
  // (not just on the live rows) so that clearing every row keeps the invoice in per-item mode and
  // takes the amount to 0. Keying off `hasGcpMaterialEntries` alone would flip back to the stale
  // seeded lump sum — a value the office can't even correct, since buildCustomRows hides that field
  // for per-item invoices — while every mission's `extraCosts` went out empty. The loaded-invoice
  // side also covers T&M mileage: the service folds those rows into the same `gcpMaterialsCosts`
  // aggregate this field is seeded from, and the per-day Mileage inputs already bill them.
  const usePerItemGcpMaterials = hasGcpMaterialEntries || hasDerivedGcpMaterialsCosts(raw);

  const gcpMaterialsLumpOrEntries = usePerItemGcpMaterials
    ? gcpMaterialEntriesTotal
    : parseFloat(fixValues.gcpMaterialsCosts) || 0;

  // What the fixed-fee "GCP Services" section header shows: the live per-item prices plus the
  // materials/mileage rows rendered under them — i.e. the sum of what's actually listed inside the
  // section, matching buildSections' own `amount + extraCosts` total that the readonly view displays.
  // The override exists only because the office can edit each price, so the loaded `section.total`
  // would be stale; leaving the entries out of it made this one line disagree with the same invoice
  // in review. The legacy lump sum stays out on purpose: it renders as its own "GCP Materials" row
  // (buildCustomRows), not inside this section.
  const gcpSectionTotal = gcpServicesTotal + gcpMaterialEntriesTotal;

  const totalNum = isFixedFee
    ? sections.filter((s) => s.id !== 'gcp_services').reduce((s, sec) => s + (sec.total ?? 0), 0) +
      gcpServicesTotal +
      gcpMaterialsLumpOrEntries -
      (parseFloat(fixValues.equipmentLeasingExpenses) || 0) +
      fixOtherTotal
    : fixDateGroups.reduce((s, g) => {
        const p = fixDatePrices[g.dateKey];
        return (
          s + parseFloat(p?.flightsCosts || '0') + parseFloat(p?.mileage || '0') + (gcpGroupTotals.get(g.dateKey) ?? 0)
        );
      }, 0) +
      gcpMaterialsLumpOrEntries -
      (parseFloat(fixValues.equipmentLeasingExpenses) || 0) +
      fixOtherTotal;
  const totalDisplay = `$${totalNum.toLocaleString()}`;
  const isZeroTotal = totalNum === 0;

  const toggle = (id: string, defaultExpanded = true) =>
    setExpanded((prev) => ({ ...prev, [id]: !(prev[id] ?? defaultExpanded) }));

  function handleFixChange(key: keyof InvoicingFixValues, value: string) {
    setFixValues((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmitFix() {
    if (!fixPdfLinkRef.current || isZeroTotal) {
      setFixSubmitAttempted(true);
      return;
    }
    trackAction('Submit fix invoice');
    setIsSubmittingFix(true);
    try {
      if (!raw) {
        setIsSubmittingFix(false);
        return;
      }

      const toNum = (v: string) => {
        const n = parseFloat(v);
        return isNaN(n) ? 0 : n;
      };
      const additionalCosts = isSingleBlankFixItem
        ? undefined
        : fixOtherItems.filter((i) => i.label.trim()).map((i) => ({ description: i.label, amount: toNum(i.price) }));
      const sectionTotal = (id: string) => sections.find((s) => s.id === id)?.total ?? 0;
      const payload = isFixedFee
        ? {
            scanCosts: sectionTotal('scan') || undefined,
            panoCosts: sectionTotal('pano') || undefined,
            lidarCosts: sectionTotal('lidar') || undefined,
            gcpCosts: gcpServicesTotal || undefined,
          }
        : {
            timeMaterialsFlightCosts:
              fixDateGroups.reduce((s, g) => s + parseFloat(fixDatePrices[g.dateKey]?.flightsCosts || '0'), 0) ||
              undefined,
            gcpCosts: fixDateGroups.reduce((s, g) => s + (gcpGroupTotals.get(g.dateKey) ?? 0), 0) || undefined,
          };
      // Resubmit fully replaces the invoice, so send the complete content — the period plus the
      // per-mission line items and (for T&M) day entries — not just the edited aggregates.
      // Materials/mileage come from `gcpMaterialEntries` (what the form currently holds), not from
      // `m.extraCosts` (the invoice as it was loaded) — reading the latter would drop every entry
      // the office added, edited or removed in this session from the payload.
      const materialsMileageFor = (m: AssociatedMission) => {
        const entryKey = m.airtableRecordId ? gcpEntryKeyByRecordId.get(m.airtableRecordId) : undefined;

        return toGcpExtraCosts(entryKey ? gcpMaterialEntries[entryKey] : undefined);
      };

      // Each GCP mission's price, keyed by airtableRecordId, taken from the same override-aware
      // source as the aggregate `gcpCosts` above (office edits, plus rate×hours for rate-set items).
      // Since resubmit is a full replace, the per-line cost must carry the edited price too — reading
      // the raw `m.cost` here would persist an invoice whose GCP line items disagree with its total.
      const gcpCostByRecordId = new Map<string, number>();
      for (const item of gcpPriceableItems) {
        if (item.airtableRecordId != null)
          gcpCostByRecordId.set(
            item.airtableRecordId,
            getGcpItemPrice(item.id, gcpDefaultAmountById.get(item.id) ?? 0),
          );
      }

      // All services are listed as the canonical set of work. Fixed fee: every mission keeps its
      // cost. T&M: scan/pano/lidar ride along at cost 0 (billed per-day via timeMaterialsEntries, so
      // a real cost here would double-count in the service's totals); GCP carries its priced amount.
      // A mission that has failed since submission stays in the list but at cost 0. These line items
      // are the only thing the service bills from — it derives BOTH submittedTotals and
      // calculatedTotals from them (of the aggregates we send it reads just
      // equipmentLeasingExpenses) and has no notion of failure. This UI excludes failed items from
      // every total it shows (buildSections and gcpPriceableItems both filter them out), so a real
      // cost here would store an invoice worth more than the office was ever shown or agreed to.
      const associatedMissions: AssociatedMissionPayload[] = raw.associatedMissions
        .filter((m) => !!m.airtableRecordId)
        .map((m) => {
          const isGcp = m.category === 'gcp_services';
          const extraCostsForMission = isGcp ? materialsMileageFor(m) : undefined;
          const costBasis = isGcp
            ? toGcpCostBasis(m.consultantGcpServiceHourlyRate, m.consultantGcpServiceTimeInHours)
            : undefined;
          const cost = m.isFailed
            ? 0
            : isGcp
              ? gcpCostByRecordId.get(m.airtableRecordId as string) ?? m.cost
              : isFixedFee
                ? m.cost
                : 0;

          return {
            kind: m.category,
            airtableRecordId: m.airtableRecordId as string,
            date: m.flightDate,
            cost,
            itemCount: m.itemCount,
            account: m.account,
            project: m.project,
            airtableProjectRecordId: m.airtableProjectRecordId,
            ...(costBasis ? { costBasis } : {}),
            ...(extraCostsForMission ? { extraCosts: extraCostsForMission } : {}),
          };
        });

      const timeMaterialsEntries: TimeMaterialsEntryPayload[] | undefined = isFixedFee
        ? undefined
        : fixDateGroups.flatMap((g) => {
            const prices = fixDatePrices[g.dateKey];
            // Resubmit replaces the invoice wholesale and keeps failed missions in its canonical
            // list of work (see associatedMissions above), so flight ids stay referenced here too.
            // GCP ids track the amount instead: gcpGroupTotals excludes failed items, so referencing
            // one would dedupe away a cost this row doesn't carry (it rides along at 0 regardless).
            const billableGcpItems = g.gcpItems.filter((item) => !item.isFailed);

            return buildTimeMaterialsDayEntries({
              dateKey: g.dateKey,
              flights: parseFloat(prices?.flightsCosts || '0') || 0,
              gcpServices: gcpGroupTotals.get(g.dateKey) ?? 0,
              gcpMaterials: sumGcpExtraCosts(billableGcpItems.map(materialsMileageFor)),
              mileage: parseFloat(prices?.mileage || '0') || 0,
              flightRefIds: collectMissionRefIds(g.scanPanoLidarItems),
              gcpRefIds: collectMissionRefIds(billableGcpItems),
            });
          });

      await getPilotApiService().updateInvoice(invoice.id, {
        startDate: raw.startDate,
        endDate: raw.endDate,
        ...payload,
        gcpMaterialsCosts: usePerItemGcpMaterials
          ? gcpMaterialEntriesTotal || undefined
          : toNum(fixValues.gcpMaterialsCosts),
        associatedMissions,
        timeMaterialsEntries: timeMaterialsEntries?.length ? timeMaterialsEntries : undefined,
        equipmentLeasingExpenses: toNum(fixValues.equipmentLeasingExpenses),
        additionalCosts,
        paymentLink: paymentMethod === 'link' ? fixValues.paymentLink || undefined : undefined,
        bankDetails: paymentMethod === 'bank' ? fixValues.bankDetails || undefined : undefined,
        invoiceLink: fixPdfLink || undefined,
        invoicePath: fixPdfPath || undefined,
        invoiceFileName: fixPdfName,
        invoiceFileSize: fixPdfSize,
        invoiceFileType: fixPdfType,
      });
      message.success({ title: 'Invoice updated', closable: true, theme: 'dark' });
      onBack();
    } catch (err) {
      Logger.error(err as Error | string);
      message.error({
        title: 'Failed to update invoice',
        description: 'Please try again.',
        closable: true,
        theme: 'dark',
      });
      setIsSubmittingFix(false);
    }
  }

  const css = { ...sharedInvoicingCss, ...localCss };

  return (
    <div className={css.page}>
      <InvoicePageHeader
        title={invoice.invoiceName}
        onBack={() => {
          trackAction('Navigate back from invoice detail');
          onBack();
        }}
        badge={
          <span style={{ flexShrink: 0 }}>
            <Badge label={invoice.status} size="l" {...getStatusBadgeProps(invoice.status)} />
          </span>
        }
      />

      <div className={css.periodSection}>
        <div className={css.periodLabel}>INVOICE PERIOD</div>
        <div className={localCss.periodDates}>{invoice.period}</div>
      </div>

      {raw?.notesForPilot && <div className={localCss.fixNote}>{raw.notesForPilot}</div>}

      <div className={css.costCard}>
        {isFixedFee
          ? sections.map((section) => {
              const isSectionExpanded = expanded[section.id] !== false;
              const isGcpServices = section.id === 'gcp_services';
              return (
                <div key={section.id}>
                  <SectionHeader
                    section={isGcpServices ? { ...section, total: gcpSectionTotal } : section}
                    isExpanded={isSectionExpanded}
                    onToggle={() => toggle(section.id)}
                  />
                  {isSectionExpanded &&
                    section.items.map((item) => {
                      if (!isGcpServices) return <ItemRow key={item.id} item={item} />;
                      const gcpKey = item.gcpServiceId ?? item.id;
                      const entries = gcpMaterialEntries[gcpKey] ?? [];
                      const hasMaterials = entries.some((e) => e.type === 'materials');
                      const hasMileage = entries.some((e) => e.type === 'mileage');
                      // Once the consultant's hourly rate and confirmed hours are both known, the
                      // price is fully auto-calculated and shown read-only; otherwise it's editable.
                      const isRateFullySet = isGcpRateFullySet(
                        item.consultantGcpServiceHourlyRate,
                        item.consultantGcpServiceTimeInHours,
                      );
                      return (
                        <div key={item.id}>
                          <ItemRow
                            item={item}
                            amountInput={
                              item.status !== 'failed' && !isRateFullySet
                                ? gcpItemPrices[item.id] ?? String(gcpDefaultAmountById.get(item.id) ?? 0)
                                : undefined
                            }
                            onAmountChange={(val) => updateGcpItemPrice(item.id, val)}
                            actionNode={
                              item.status !== 'failed' ? (
                                <ExtraCostMenuButton
                                  hasMaterials={hasMaterials}
                                  hasMileage={hasMileage}
                                  onSelect={(type) => addGcpMaterial(gcpKey, type)}
                                />
                              ) : undefined
                            }
                          />
                          {entries.map((entry) => (
                            <GcpMaterialRow
                              key={entry.id}
                              label={entry.type === 'materials' ? 'GCP Materials' : 'Mileage'}
                              amount={entry.amount}
                              onAmountChange={(val) => updateGcpMaterialAmount(gcpKey, entry.id, val)}
                              onRemove={() => removeGcpMaterial(gcpKey, entry.id)}
                            />
                          ))}
                        </div>
                      );
                    })}
                </div>
              );
            })
          : null}
        {!isFixedFee &&
          fixDateGroups.map((group) => {
            const isDateExpanded = expanded[group.dateKey] !== false;
            const isFlightsExpanded = expanded[`${group.dateKey}-flights`] === true;
            const isGcpExpanded = expanded[`${group.dateKey}-gcp`] === true;
            const prices = fixDatePrices[group.dateKey] ?? { flightsCosts: '', mileage: '' };

            const setDatePrice = (field: keyof TimeMaterialsDatePrices, val: string) =>
              setFixDatePrices((prev) => ({
                ...prev,
                [group.dateKey]: { ...(prev[group.dateKey] ?? { flightsCosts: '', mileage: '' }), [field]: val },
              }));

            const gcpMaterialsTotalForDate = gcpMaterialsTotalsByDate.get(group.dateKey) ?? 0;

            const dateTotal =
              parseFloat(prices.flightsCosts || '0') +
              parseFloat(prices.mileage || '0') +
              gcpMaterialsTotalForDate +
              (gcpGroupTotals.get(group.dateKey) ?? 0);

            const flightsSubtitle = buildFlightsSubtitle(group.scanPanoLidarItems);
            const gcpSubtitle = buildGcpSubtitle(group.gcpItems);

            return (
              <TimeMaterialsDateHeader
                key={group.dateKey}
                displayDate={group.displayDate}
                total={dateTotal}
                isExpanded={isDateExpanded}
                onToggle={() => toggle(group.dateKey)}
              >
                {group.scanPanoLidarItems.length > 0 && (
                  <TimeMaterialsCategoryHeader
                    title="Scans, panoramas price"
                    subtitle={flightsSubtitle || undefined}
                    isExpanded={isFlightsExpanded}
                    onToggle={() => toggle(`${group.dateKey}-flights`, false)}
                    price={prices.flightsCosts}
                    onPriceChange={(val) => setDatePrice('flightsCosts', val)}
                  >
                    {groupBySiteSummary(group.scanPanoLidarItems).map((merged, idx, arr) => (
                      <ItemRow
                        key={idx}
                        hideDate
                        isLast={idx === arr.length - 1}
                        item={{
                          id: String(idx),
                          date: '',
                          siteName: merged.project,
                          customer: merged.account,
                          status: merged.isFailed ? 'failed' : merged.isOnReview ? 'on_review' : 'billable',
                          rateInfo: merged.summary,
                        }}
                      />
                    ))}
                  </TimeMaterialsCategoryHeader>
                )}

                {group.gcpItems.length > 0 && (
                  <TimeMaterialsCategoryHeader
                    title="GCP services"
                    subtitle={gcpSubtitle}
                    isExpanded={isGcpExpanded}
                    onToggle={() => toggle(`${group.dateKey}-gcp`, false)}
                    amount={(gcpGroupTotals.get(group.dateKey) ?? 0) + gcpMaterialsTotalForDate}
                  >
                    {group.gcpItems.map((item, idx, arr) => {
                      const gcpServiceId = item.gcpServiceId ?? item.id;
                      const entries = gcpMaterialEntries[gcpServiceId] ?? [];
                      const hasMaterials = entries.some((e) => e.type === 'materials');
                      // Once the consultant's hourly rate and confirmed hours are both known, the
                      // price is fully auto-calculated and shown read-only; otherwise it's editable.
                      const isRateFullySet = isGcpRateFullySet(
                        item.consultantGcpServiceHourlyRate,
                        item.consultantGcpServiceTimeInHours,
                      );
                      return (
                        <div key={item.id}>
                          <ItemRow
                            hideDate
                            isLast={idx === arr.length - 1}
                            item={{
                              id: item.id,
                              date: '',
                              siteName: item.project,
                              customer: item.account,
                              status: item.isFailed ? 'failed' : 'billable',
                              category: item.category,
                              itemCount: item.itemCount,
                              rateInfo: item.isFailed
                                ? undefined
                                : buildConsultantGcpRateInfo(
                                    item.consultantGcpServiceHourlyRate,
                                    item.consultantGcpServiceTimeInHours,
                                  ),
                              amount:
                                !item.isFailed && isRateFullySet
                                  ? getGcpItemAmount(
                                      item.cost,
                                      item.consultantGcpServiceHourlyRate,
                                      item.consultantGcpServiceTimeInHours,
                                    )
                                  : undefined,
                            }}
                            amountInput={
                              item.isFailed || isRateFullySet
                                ? undefined
                                : gcpItemPrices[item.id] ?? String(gcpDefaultAmountById.get(item.id) ?? 0)
                            }
                            onAmountChange={(val) => updateGcpItemPrice(item.id, val)}
                            actionNode={
                              !item.isFailed && !hasMaterials ? (
                                <Button
                                  size="xs"
                                  text="GCP Materials"
                                  style="secondary"
                                  icon={<PlusIcon size={14} color={Blue100} />}
                                  onClick={() => addGcpMaterial(gcpServiceId, 'materials')}
                                />
                              ) : undefined
                            }
                          />
                          {entries.map((entry) => (
                            <GcpMaterialRow
                              key={entry.id}
                              timeMaterials
                              label={entry.type === 'materials' ? 'GCP Materials' : 'Mileage'}
                              amount={entry.amount}
                              onAmountChange={(val) => updateGcpMaterialAmount(gcpServiceId, entry.id, val)}
                              onRemove={() => removeGcpMaterial(gcpServiceId, entry.id)}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </TimeMaterialsCategoryHeader>
                )}

                <TimeMaterialsCategoryHeader
                  title="Mileage"
                  price={prices.mileage}
                  onPriceChange={(val) => setDatePrice('mileage', val)}
                />
              </TimeMaterialsDateHeader>
            );
          })}

        <div>
          <SectionHeader
            section={{ id: 'other-costs', label: 'Other', items: [], total: fixOtherTotal }}
            isExpanded={expanded['other-costs'] ?? true}
            onToggle={() => toggle('other-costs')}
            subtitle="Additional expenses"
            className={classes(
              css.otherSectionHeader,
              css.otherSectionHeaderEditable,
              (expanded['other-costs'] ?? true) && css.otherSectionHeaderExpanded,
            )}
          />
          {(expanded['other-costs'] ?? true) && (
            <OtherItemsList
              items={fixOtherItems}
              onRemove={removeFixOtherItem}
              onLabelChange={updateFixOtherLabel}
              onPriceChange={updateFixOtherPrice}
              onAdd={addFixOtherItem}
            />
          )}
        </div>

        {customRows.length > 0 && (
          <>
            {customRows.map((field) => (
              <div key={field.id} className={css.customFieldRow}>
                <div className={css.customFieldInfo}>
                  <span className={css.customFieldLabel}>{field.label}</span>
                  <span className={css.customFieldDesc}>{field.desc}</span>
                </div>
                {field.fixKey && (
                  <div className={css.priceInputContainer}>
                    <TextInput
                      leftCell={fixValues[field.fixKey] ? '$' : undefined}
                      className={localCss.fixInlineInput}
                      value={fixValues[field.fixKey]}
                      onChange={(val: string) => handleFixChange(field.fixKey!, numericOnly(val))}
                    />
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        <div className={css.cardDivider} />
        <div className={css.totalRow}>
          <span className={css.totalLabel}>Total</span>
          <span className={css.totalAmount} style={fixSubmitAttempted && isZeroTotal ? { color: Red90 } : undefined}>
            {totalDisplay}
          </span>
        </div>
        {fixSubmitAttempted && isZeroTotal && <p className={localCss.totalError}>The total cannot be zero</p>}
      </div>

      <div className={css.outerSection}>
        <div className={css.outerSectionHead}>
          <div className={css.outerSectionTitle}>Upload Invoice</div>
          <div className={css.outerSectionSubtitle}>Must match the items listed above</div>
        </div>
        {fixPdfLink || fixPdfUploading ? (
          <FileUploadItem
            file={{
              uid: fixPdfLink ?? 'uploading',
              name: fixPdfName ?? 'Invoice',
              status: fixPdfUploading ? 'uploading' : 'done',
            }}
            onDownload={
              fixPdfLink
                ? () => {
                    trackAction('Download invoice PDF');
                    window.open(fixPdfLink, '_blank');
                  }
                : undefined
            }
            onRemove={handleFixPdfDelete}
          />
        ) : (
          <FileUpload
            onChange={(info) => {
              const file = info.file.originFileObj;
              if (file) void handleFixPdfChange(file);
            }}
            accept=".pdf"
            isError={fixSubmitAttempted}
            errorMessage="Upload an invoice"
          />
        )}
      </div>

      <div className={css.outerSection}>
        <div className={css.outerSectionHead}>
          <div className={css.outerSectionTitle}>Payment info</div>
          <div className={css.outerSectionSubtitle}>Select the payment method you prefer</div>
        </div>
        <PaymentSection
          method={paymentMethod}
          paymentLink={fixValues.paymentLink}
          bankDetails={fixValues.bankDetails}
          onMethodChange={setPaymentMethod}
          onLinkChange={(val) => handleFixChange('paymentLink', val)}
          onBankChange={(val) => handleFixChange('bankDetails', val)}
        />
      </div>

      <div className={css.footer}>
        <div className={css.footerNoteWrap}>
          <p className={css.footerNote}>
            Once submitted, you can&apos;t edit this invoice. Reviewed by Operations Manager before payment.
          </p>
        </div>
        <Button
          style="primary"
          size="m"
          disabled={isSubmittingFix}
          onClick={handleSubmitFix}
          text={isSubmittingFix ? 'Submitting…' : 'Submit Invoice'}
        />
      </div>
    </div>
  );
};

const localCss = stylesheet({
  periodDates: { ...typeCompact14MediumProperties, color: Black },
  fixInlineInput: { width: 90 },
  totalError: {
    ...typeCompact12RegularProperties,
    padding: '0 16px 12px',
    color: Red90,
    textAlign: 'right' as const,
    margin: 0,
  },
  // Reason the invoice was sent back for corrections (notesForPilot from the service), shown to
  // the pilot as a gold callout above the cost breakdown.
  fixNote: {
    ...typeCompact14MediumProperties,
    color: Gold90,
    background: Gold0,
    border: `1px solid ${Gold20}`,
    borderRadius: 12,
    padding: 12,
    marginBottom: 16,
    wordBreak: 'break-word',
  },
});
